import { useCallback } from "react";
import { CherryPayApi } from "../api/models";
import { useApiClient } from "./useApiClient";
import { useToast } from "./useToast";

interface UseNotificationDeleteOptions {
  businessId: string;
  selectedNotification: CherryPayApi.CherryPassNotificationSchedule | null;
  setSelectedNotification: (
    notification: CherryPayApi.CherryPassNotificationSchedule | null
  ) => void;
  refresh: () => void;
  showConfirmationDialog: (
    title: string,
    message: string,
    confirmText: string,
    cancelText: string
  ) => Promise<{ ok: boolean }>;
}

export const useNotificationDelete = (
  options: UseNotificationDeleteOptions
) => {
  const {
    businessId,
    selectedNotification,
    setSelectedNotification,
    refresh,
    showConfirmationDialog,
  } = options;

  const apiClient = useApiClient();
  const { showInfoToast, showErrorToast, showSuccessToast } = useToast();

  return useCallback(
    async (notification: CherryPayApi.CherryPassNotificationSchedule) => {
      if (!notification.Meta?.Id) {
        showErrorToast(
          "Unable to delete notification: missing notification ID"
        );
        return;
      }

      showConfirmationDialog(
        "Are you sure you want to delete this notification?",
        `This action will delete the notification permanently. Click Confirm to proceed or Cancel to go back.`,
        "Confirm",
        "Cancel"
      ).then(({ ok }) => {
        if (ok && notification.Meta?.Id) {
          showInfoToast("Deleting notification...");

          apiClient
            .deleteCherryPassNotificationSchedule(
              businessId,
              notification.Meta.Id
            )
            .then((result) => {
              if (result.ok) {
                showSuccessToast("Notification deleted successfully");

                // Clear selected notification if it was the deleted one
                if (selectedNotification?.Meta?.Id === notification.Meta?.Id) {
                  setSelectedNotification(null);
                }

                // Refresh the list to remove the deleted notification
                refresh();
              } else {
                showErrorToast(
                  "Failed to delete notification: " + result.message
                );
              }
            })
            .catch((error) => {
              showErrorToast("Failed to delete notification");
              console.error("Delete notification error:", error);
            });
        }
      });
    },
    [
      businessId,
      apiClient,
      showErrorToast,
      showSuccessToast,
      showConfirmationDialog,
      selectedNotification,
      setSelectedNotification,
      refresh,
    ]
  );
};
